import { FC, useRef, useState } from 'react'

import { Box, SearchField, Title } from '@factorialco/deprecated-design-system'
import {
  GroupDetailInfoDocument,
  PeopleChartEmployeesDocument,
  PeopleChartGroupSidepanelBasicInfoFragment,
  PeopleChartMembersDetailFragment,
  PeopleChartRolesDocument,
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import MultipleChoice from 'modules/core/components/Filter/filters/MultipleChoice'
import SingleChoiceFilter from 'modules/core/components/Filter/filters/SingleChoice'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import MemberRow from './MemberRow'
import Row from './Row'
import { useAddMember } from './useAddMember'
import { useDeleteMember } from './useDeleteMember'
import { useUpdateMembership } from './useUpdateTeamMembership'

type Props = {
  members: PeopleChartMembersDetailFragment
  team: PeopleChartGroupSidepanelBasicInfoFragment
}

const EmployeeSelector: FC<Props> = ({ team, members }) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [openSelector, setOpenSelector] = useState<boolean>(false)
  const [employeeStatus, setEmployeeStatus] = useState<string | undefined>('unassigned')
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])

  const { addMember } = useAddMember()
  const { deleteMember } = useDeleteMember()
  const { updateMembership } = useUpdateMembership()
  const wrapperRef = useRef<HTMLElement | null>(null)
  const { data, loading } = useQuery(PeopleChartEmployeesDocument)
  const employees = useUnsafeMemo(() => data?.employees.activeEmployees || [], [data])
  const { data: roles, loading: rolesLoading } = useQuery(PeopleChartRolesDocument)
  const isFilterOpen = useRef(false)

  useUnsafeEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !(wrapperRef.current.contains(event.target as Node) || isFilterOpen.current)
      ) {
        setOpenSelector(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef])

  useUnsafeEffect(() => {
    const hasUnassignedEmployees = employees.some((employee) => employee.memberships.length === 0)

    if (hasUnassignedEmployees) {
      setEmployeeStatus('unassigned')
    } else {
      setEmployeeStatus('assigned')
    }
  }, [employees])

  const filteredEmployees = employees.filter((employee) => {
    const employeeName = employee.fullName.toLowerCase() || ''
    const filterByName = employeeName.includes(search.toLowerCase())
    const roleId = employee.referenceContract?.jobCatalogLevel?.role?.id

    const filterByRole = !selectedRoles.length || (roleId && selectedRoles.includes(roleId))

    const hasNoMemberships = employee.memberships.length === 0

    const filterByStatus =
      employeeStatus === 'unassigned' ? hasNoMemberships : employee.memberships.length > 0

    return filterByName && filterByRole && filterByStatus
  })

  const handleOnSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearch(event)
    setOpenSelector(true)
  }

  const handleSearchClick = async () => {
    setOpenSelector(true)
  }

  const rolesOptions = roles?.jobCatalog.rolesConnection.nodes.map((role) => {
    return { label: role.name, value: role.id }
  })

  const handleMemberAdditon = async (employeeId: string) => {
    const result = await addMember({
      variables: {
        employeeId: employeeId,
        teamId: team.id,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GroupDetailInfoDocument,
        },
      ],
    })
    const handled = handleResult(result, (errors: Record<string, string>) => {
      if (errors.messages) {
        app.setFlash({
          type: 'wrong',
          message: errors.messages,
        })
      }
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('people_chart.employees_selector.success_add_member'),
      })
    }

    if (!handled.success) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('people_chart.employees_selector.error_add_member'),
      })
    }
    return handled
  }

  const membershipId = (employeeId: string): string | null => {
    const membership = members.activeMemberships.nodes.find(
      (x) => x.employee && x.employee.id === employeeId
    )
    return membership ? membership.id : null
  }

  const deleteMembership = async (employeeId: string) => {
    const membership = membershipId(employeeId)
    if (membership) {
      const result = await deleteMember({
        variables: {
          id: membership,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GroupDetailInfoDocument,
          },
        ],
      })
      const handled = handleResult(result, () => undefined)
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('people_chart.employees_selector.success_remove_member'),
        })
      }
      if (!handled.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('people_chart.employees_selector.error_remove_member'),
        })
      }
    }
  }

  const updateTeamMembership = async (membership: string, lead: boolean) => {
    if (membership) {
      const result = await updateMembership({
        variables: {
          id: membership,
          lead: lead,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GroupDetailInfoDocument,
          },
        ],
      })
      const handled = handleResult(result, () => undefined)
      if (!handled.success) {
        const error_adding_leader = i18n.t('people_chart.employees_selector.error_add_leader')
        const error_removing_leader = i18n.t(
          'people_chart.employees_selector.success_remove_member'
        )

        app.setFlash({
          type: 'wrong',
          message: lead ? error_adding_leader : error_removing_leader,
        })
      }

      if (handled.success) {
        const success_adding_leader = i18n.t('people_chart.employees_selector.success_add_leader')
        const success_removing_leader = i18n.t(
          'people_chart.employees_selector.success_remove_leader'
        )
        app.setFlash({
          type: 'correct',
          message: lead ? success_adding_leader : success_removing_leader,
        })
      }
    }
  }

  const isTeamLeader = members.activeMemberships.nodes.some((member) => member.lead)
  const handleFilterToggle = (value: boolean) => {
    isFilterOpen.current = value
  }
  return (
    <Box
      borderRadius={{
        all: 'abs016',
      }}
      paddingTop="s16"
      paddingX="s16"
      gap="s8"
    >
      <Title
        variant="400"
        title={i18n.t('people_chart.employees_selector.count_members', {
          count: members.activeMemberships.nodes.length,
        })}
      />
      <Box>
        <SearchField
          onSearch={handleOnSearch}
          placeholder={i18n.t('people_chart.employees_selector.add_members')}
          search={search}
          loading={search !== debouncedSearch}
          onClick={handleSearchClick}
        />
      </Box>
      <Box gap="s8" paddingBottom="s16">
        {!openSelector && !isTeamLeader && members.activeMemberships.nodes.length > 0 && (
          <Callout
            description={i18n.t('people_chart.employees_selector.leader_callout.description')}
            title={i18n.t('people_chart.employees_selector.leader_callout.title')}
            type="info"
          />
        )}
        {!openSelector &&
          members.activeMemberships.nodes.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              updateMembership={() => updateTeamMembership(member.id, !member.lead)}
              deleteMembership={() => deleteMembership(member.employee.id)}
            />
          ))}
      </Box>
      {openSelector && (
        <Box
          ref={wrapperRef}
          border={{
            all: {
              color: 'grey400',
              width: 's1',
              style: 'solid',
            },
          }}
          position="absolute"
          background="white"
          style={{ top: '24%', maxWidth: '540px', width: '540px' }}
          borderRadius={{
            all: 'abs016',
          }}
          padding={'s16'}
        >
          <Box overflowY="auto" height="s300">
            <Box flexDirection="row" alignItems="center" justifyContent="flexEnd" gap="s8">
              <SingleChoiceFilter
                label={i18n.t('people_chart.employees_selector.team_status')}
                options={[
                  {
                    value: 'unassigned',
                    label: i18n.t('people_chart.employees_selector.unassigned'),
                  },
                  {
                    value: 'assigned',
                    label: i18n.t('people_chart.employees_selector.assigned'),
                  },
                ]}
                value={employeeStatus}
                onValueChange={setEmployeeStatus}
                onToggle={handleFilterToggle}
                onClose={() => handleFilterToggle(false)}
              />
              {!rolesLoading && rolesOptions && rolesOptions?.length > 0 && (
                <MultipleChoice<string>
                  hideSearch
                  label={i18n.t('people_chart.employees_selector.role')}
                  options={rolesOptions}
                  value={selectedRoles}
                  onValueChange={(ids = []) => setSelectedRoles(ids)}
                  onToggle={handleFilterToggle}
                  onClose={() => handleFilterToggle(false)}
                />
              )}
            </Box>
            {filteredEmployees.map((employee) => (
              <Row
                key={employee.id}
                employee={employee}
                onAddMember={() => handleMemberAdditon(employee.id)}
                membershipId={membershipId(employee.id)}
                deleteMembership={() => deleteMembership(employee.id)}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default EmployeeSelector
